import { EditFormIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { deleteCenterCourse } from '../../../../api/centers/center-courses/delete-center-course'
import { updateCenterCourse } from '../../../../api/centers/center-courses/update-center-course'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CenterCourse } from '../../../../lib/types/models/center-course/center-course'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { queryClient } from '../../../../main'
import { mapCenterCourseToInput } from '../utils'
import CenterCourseForm from './center-course-form'

type Props = {
  isOpen: boolean
  startDate?: Date
  endDate?: Date
  centerId: number
  data: CenterCourse
  allowDelete?: boolean
  closeModel: () => void
}

const CenterCourseEditFormModal = ({
  isOpen,
  data,
  centerId,
  startDate,
  endDate,
  allowDelete,
  closeModel
}: Props) => {
  const { t, currentLanguage } = useTranslate()
  const updatecourse = useMutation({
    mutationKey: ['update-center-course', data.id],
    mutationFn: updateCenterCourse,
    onSuccess: () => {
      toast.success(t('ol.center.program.course.form.edit.success.message'))
      queryClient.invalidateQueries({ queryKey: ['center-courses', centerId] })
      closeModel()
    },
    onError: showAxiosError
  })

  const deletecourse = useMutation({
    mutationKey: ['delete-center-course', data.id],
    mutationFn: deleteCenterCourse,
    onSuccess: () => {
      toast.success(t('ol.center.program.course.form.delete.success.message'))
      queryClient.invalidateQueries({ queryKey: ['center-courses', centerId] })
      //delete is inside the edit modal, so we close it after deletion
      closeModel()
    },
    onError: showAxiosError
  })
  return (
    <Modal
      isOpen={isOpen}
      title={t('ol.center.program.course.form.update.title')}
      icon={EditFormIcon}
      hasBorderBottom
      hideActions
      size="medium"
      onCancel={() => closeModel()}
    >
      <CenterCourseForm
        className={MODAL_CONTENT_CLASSNAME}
        startDate={startDate}
        endDate={endDate}
        defaultValues={mapCenterCourseToInput(data)}
        buttonsConfig={modalButtonConfig({
          isLoading: updatecourse.isPending,
          isDeleteLoading: deletecourse.isPending,
          deleteText: t('ol.center.program.course.form.delete.button'),
          onCancel: () => closeModel(),
          onDelete: allowDelete
            ? async () => {
                await deletecourse.mutateAsync({ centerId, id: data.id })
              }
            : undefined,
          onConfirm: async (values) =>
            updatecourse.mutateAsync({ centerId, id: data.id, ...values }),
          isEnglish: currentLanguage === 'en' ? true : false
        })}
      />
    </Modal>
  )
}

export default CenterCourseEditFormModal
